/* The sync loop.
 *
 * Pull, merge, then push whatever is still dirty — in that order, always.
 * Pulling first means conflicts resolve locally, against a store that already
 * knows how to keep the losing side, and the resolution goes up as ordinary
 * records. Pushing first would hand the server a change it has no basis to
 * reconcile.
 *
 * The engine never touches records directly; every incoming change goes through
 * `store.merge()`, which owns the conflict policy. This file owns *when* and
 * *what to trust*, nothing else.
 *
 * A transport is:
 *   pull(cursor) -> { records, cursor }
 *   push(records) -> { cursor }
 * Both may throw; nothing is marked synced when they do.
 */

import { KINDS } from './store.js';

const CURSOR_KEY = 'syncCursor';
const IDLE_MS = 30_000;      // heartbeat when nothing is happening
const AFTER_EDIT_MS = 2_500; // settle time after you stop typing
const MAX_BACKOFF_MS = 5 * 60_000;

const count = r => KINDS.reduce((a, k) => a + (r[k]?.length || 0), 0);

export function createSync(store, transport, { onStatus = () => {}, intervalMs = IDLE_MS } = {}) {
  let running = false;
  let inFlight = null;
  let timer = null;
  let failures = 0;
  let unsubscribe = null;
  let editTimer = null;

  const state = { phase: 'idle', lastSyncedAt: null, lastError: null, pending: 0 };

  function emit(patch) {
    Object.assign(state, patch);
    onStatus({ ...state });
  }

  function pendingCount() {
    return count(store.pending());
  }

  /**
   * One full exchange. Concurrent callers share the in-flight promise rather
   * than stacking, so a burst of triggers cannot interleave two pushes.
   */
  function sync() {
    if (inFlight) return inFlight;
    inFlight = run().finally(() => { inFlight = null; });
    return inFlight;
  }

  async function run() {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      emit({ phase: 'offline', pending: pendingCount() });
      return { skipped: 'offline' };
    }

    emit({ phase: 'syncing', lastError: null });
    try {
      const pulled = await pullOnce();
      const pushed = await pushOnce();
      failures = 0;
      emit({
        phase: 'idle',
        lastSyncedAt: new Date().toISOString(),
        lastError: null,
        pending: pendingCount()
      });
      await store.flush();
      return { pulled, pushed };
    } catch (err) {
      failures++;
      emit({ phase: 'error', lastError: err.message, pending: pendingCount() });
      throw err;
    }
  }

  async function pullOnce() {
    const cursor = store.getMeta(CURSOR_KEY) ?? 0;
    const { records = {}, cursor: next } = await transport.pull(cursor) || {};
    const incoming = count(records);
    let merged = null;

    if (incoming) {
      merged = store.merge(records);

      // Anything the merge accepted verbatim is, by definition, what the server
      // holds — mark it clean. Anything our local copy won stays dirty and goes
      // up on the push below. Conflict copies are new records with no syncedAt,
      // so they are pending too.
      for (const kind of KINDS) for (const rec of records[kind] || []) {
        const mine = store.get(kind, rec.id);
        if (mine && mine.updatedAt === rec.updatedAt) store.markSynced(kind, rec.id, rec.updatedAt);
      }
    }

    // Only advance once the records behind the cursor are safely merged.
    if (next !== undefined && next !== null) store.setMeta(CURSOR_KEY, next);
    return { received: incoming, merged };
  }

  async function pushOnce() {
    const pending = store.pending();
    const total = count(pending);
    if (!total) return { sent: 0 };

    await transport.push(pending);

    // Mark synced against the stamp we sent, not the record as it stands now:
    // a keystroke landing mid-request must stay dirty and go up on the next
    // pass. This is why `updatedAt` has to be strictly monotonic.
    for (const kind of KINDS) for (const rec of pending[kind] || []) {
      store.markSynced(kind, rec.id, rec.updatedAt);
    }

    // Deliberately *not* advancing the cursor here. A push assigns fresh
    // sequence numbers to our own rows, and jumping the cursor to those would
    // step over anything another device wrote in between — silently, forever.
    // The cost of not advancing is pulling our own writes back once, where the
    // merge sees identical timestamps and does nothing.
    return { sent: total };
  }

  function schedule(ms) {
    clearTimeout(timer);
    if (!running) return;
    timer = setTimeout(tick, ms);
  }

  async function tick() {
    try { await sync(); } catch (e) { /* backoff below */ }
    const wait = failures
      ? Math.min(MAX_BACKOFF_MS, intervalMs * Math.pow(2, failures - 1))
      : intervalMs;
    schedule(wait);
  }

  /** A local edit should reach the server soon, but not on every keystroke. */
  function onLocalChange() {
    emit({ pending: pendingCount() });
    clearTimeout(editTimer);
    editTimer = setTimeout(() => { if (running) sync().catch(() => {}); }, AFTER_EDIT_MS);
  }

  const wake = () => { if (running) sync().catch(() => {}); };
  const onVisible = () => { if (typeof document === 'undefined' || document.visibilityState === 'visible') wake(); };

  return {
    get status() { return { ...state }; },
    sync,

    start() {
      if (running) return;
      running = true;
      failures = 0;
      unsubscribe = store.subscribe(onLocalChange);
      if (typeof addEventListener === 'function') {
        addEventListener('online', wake);
        addEventListener('visibilitychange', onVisible);
      }
      emit({ pending: pendingCount() });
      tick();
    },

    stop() {
      running = false;
      clearTimeout(timer);
      clearTimeout(editTimer);
      unsubscribe?.();
      unsubscribe = null;
      if (typeof removeEventListener === 'function') {
        removeEventListener('online', wake);
        removeEventListener('visibilitychange', onVisible);
      }
      emit({ phase: 'idle' });
    }
  };
}
